"use client";

import { create } from "zustand";
import { useEffect } from "react";
import { EMPTY_CART, type Cart, type CartBox } from "@/lib/cart/types";
import type { BoxCode } from "@/lib/catalog/types";
import { readCart, writeCart } from "@/lib/cart/storage";
import { boxMixGroup } from "@/lib/cart/cart";
import type { ServerCart } from "@/lib/cart/server-types";

// Step 7 cart store. For commits 2-6 the legacy localStorage cart is
// still the source of truth (existing add paths write through
// lib/cart/storage). The drawer mirrors that via `mag-portal-cart-changed`
// + storage events. Commit 7 promotes the DDB server cart to primary and
// keeps localStorage as the unauthenticated path.

type CartStore = {
  open: boolean;
  legacyCart: Cart;
  serverCart: ServerCart | null;
  selectedDeliveryDate: string | null;
  hydrated: boolean;
  setOpen: (next: boolean) => void;
  toggleOpen: () => void;
  refreshLegacy: () => void;
  // #468 SIZE DRIVES PACKING: choose a box size (Small/Standard/Large) for the
  // box's whole GROUP - stamps groupBoxSize on the group's lines and re-packs
  // (writeCart canonicalizes), so the group consolidates/splits and the invoice
  // + freight match. Supersedes the retired per-anchor display-only setBoxSize.
  setGroupSize: (box: CartBox, size: BoxCode) => void;
  setServerCart: (next: ServerCart | null) => void;
  setSelectedDeliveryDate: (iso: string | null) => void;
};

const DELIVERY_DATE_KEY = "mag-portal-delivery-date";

export const useCartStore = create<CartStore>()((set, get) => ({
  open: false,
  legacyCart: EMPTY_CART,
  serverCart: null,
  selectedDeliveryDate: null,
  hydrated: false,
  setOpen: (next) => set({ open: next }),
  toggleOpen: () => set((s) => ({ open: !s.open })),
  refreshLegacy: () => {
    if (typeof window === "undefined") return;
    // The stored cart is already canonicalized (writeCart re-packs to each
    // group's chosen size), so box.size + groupBoxSize are authoritative here.
    set({ legacyCart: readCart(), hydrated: true });
  },
  setGroupSize: (box, size) => {
    if (typeof window === "undefined") return;
    const group = boxMixGroup(box);
    const cart = readCart();
    const next: Cart = {
      ...cart,
      boxes: cart.boxes.map((b) =>
        boxMixGroup(b) === group
          ? {
              ...b,
              contents: b.contents.map((l) =>
                // #63 box_stem: stamp stem lines too, so tapping a size chip on
                // a hydrangea stem box re-packs its group to that size.
                l.unit === "bunch" || l.unit === "stem"
                  ? { ...l, groupBoxSize: size }
                  : l,
              ),
            }
          : b,
      ),
    };
    // writeCart canonicalizes -> the packer fills the group to `size`.
    writeCart(next);
    get().refreshLegacy();
  },
  setServerCart: (next) => set({ serverCart: next }),
  setSelectedDeliveryDate: (iso) => {
    if (typeof window !== "undefined") {
      if (iso) window.localStorage.setItem(DELIVERY_DATE_KEY, iso);
      else window.localStorage.removeItem(DELIVERY_DATE_KEY);
      window.dispatchEvent(new CustomEvent("mag-portal-cart-changed"));
    }
    set({ selectedDeliveryDate: iso });
  },
}));

export function selectTotalItems(state: CartStore): number {
  let n = 0;
  for (const box of state.legacyCart.boxes) {
    for (const line of box.contents) n += line.quantity;
  }
  return n;
}

// #landed-cost-per-stem: total stems across the cart (sum of each line's stems),
// the denominator for the landed-cost-per-stem reframe.
export function selectTotalStems(state: CartStore): number {
  let n = 0;
  for (const box of state.legacyCart.boxes) {
    for (const line of box.contents) n += line.stems;
  }
  return n;
}

export function useCartBootstrap(): void {
  useEffect(() => {
    // #468 SIZE DRIVES PACKING: the group's chosen size now lives ON the cart
    // lines (groupBoxSize) and persists with the cart itself, so there is no
    // separate size map to load - the first refresh reads the canonicalized cart.
    useCartStore.getState().refreshLegacy();
    const stored = window.localStorage.getItem(DELIVERY_DATE_KEY);
    if (stored && useCartStore.getState().selectedDeliveryDate !== stored) {
      // Direct set bypasses the persistence side-effect (already in LS).
      useCartStore.setState({ selectedDeliveryDate: stored });
    }
    function handler() {
      useCartStore.getState().refreshLegacy();
    }
    window.addEventListener("mag-portal-cart-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("mag-portal-cart-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
}
